type DateType = Date | string | number;

export const differenceInYears = (date: Date | string): number => {
  const dateDiff = new Date().getTime() - new Date(date).getTime();
  return new Date(dateDiff).getUTCFullYear() - 1970;
};

export const MmDdSwitch = (date: string): string => {
  if (!date) return '';
  let formatted_date = '';
  if (date.indexOf('/') >= 0) {
    const [mm, dd, yyyy] = date.split('/');
    formatted_date = `${dd}/${mm}/${yyyy}`;
  }
  return formatted_date;
};

const timeDifference = (date: DateType, conversion = 1) => {
  const dateDiff = new Date().getTime() - new Date(date).getTime();
  return Math.floor(dateDiff / conversion);
};

export const differenceInWeeks = (date: DateType): number => timeDifference(date, 1000 * 60 * 60 * 24 * 7);
export const differenceInDays = (date: DateType): number => timeDifference(date, 1000 * 60 * 60 * 24);
export const differenceInMinutes = (date: DateType): number => timeDifference(date, 1000 * 60);

export const isPast = (date: DateType): boolean => timeDifference(date) > 0;
export const isValid = (date: DateType): boolean => {
  const dateObj = new Date(date);
  return dateObj instanceof Date && !Number.isNaN(dateObj.getTime());
};
export const addMinutes = (date: DateType, minutes: number): number => new Date(date).getTime() + 1000 * 60 * minutes;

export const format = (date: DateType, formatObj: Intl.DateTimeFormatOptions = {}): string =>
  new Intl.DateTimeFormat('en-us', formatObj).format(new Date(date));

export const isTomorrow = (date: DateType): boolean => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return new Date(date).toDateString() === tomorrow.toDateString();
};

export const isToday = (date: DateType): boolean => new Date(date).toDateString() === new Date().toDateString();

export const formatToParts = (
  date: DateType,
  formatObj: Intl.DateTimeFormatOptions = {}
): Partial<Record<Intl.DateTimeFormatPartTypes, string>> =>
  new Intl.DateTimeFormat('en-us', formatObj).formatToParts(new Date(date)).reduce(
    (acc, part) => ({
      ...acc,
      [part.type]: part.value
    }),
    {}
  );

export const parseISO = (dateString: string): Date => {
  const isoDate = new Date(dateString);
  if (Number.isNaN(isoDate.getTime())) {
    throw new Error('Invalid ISO date string');
  }
  return isoDate;
};

export const isWithinInterval = (date: DateType, start: DateType, end: DateType): boolean => {
  const dateObj = new Date(date).getTime();
  const startObj = new Date(start).getTime();
  const endObj = new Date(end).getTime();
  return dateObj >= startObj && dateObj <= endObj;
};

export const formatReadableDateAndTime = (dateString: string, userTimeZone: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: userTimeZone,
    timeZoneName: 'short'
  }).format(date);
};

export const dateToApiDate = (date: DateType): string => {
  if (!date) return '';
  const { year, day, month } = formatToParts(date, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return `${year}-${month}-${day}`;
};

/**
 * Formats a time period from a start time and duration in minutes.
 * @param startTime - The start time as a string or Date object.
 * @param duration - The duration in minutes.
 * @returns A formatted string representing the time period.
 *
 * @example
 * formatTimePeriod('2025-10-10T10:00:00Z', 30) // "10:00am - 10:30am GMT"
 */
export const formatTimePeriod = (startTime: string | Date, duration: number) => {
  const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
  const end = new Date(start.getTime() + duration * 60000);

  const formatTimeString = (date: Date, withTimezone = false) => {
    const timeString = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: withTimezone ? 'short' : undefined
    });

    return timeString.replace(/ AM/g, 'am').replace(/ PM/g, 'pm');
  };

  return `${formatTimeString(start)} - ${formatTimeString(end, true)}`;
};
